import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useToasts } from 'react-toast-notifications'
import styled, { css, useTheme } from 'styled-components'

import { APP_DEFAULT_STATE } from '@api/local'
import { Spacer } from '@atoms/index'
import { CheckoutStepRenderer } from '@client/components/organisms/new_checkout/CheckoutStepRenderer'
import { NotFound, PlansBoxShadow, ResponsiveProperty, ResponsivePXValue } from '@client/components/Theme'
import { useConfig } from '@client/contexts/ConfigProvider'
import {
  RegisteredUserDetailsFragment,
  useGetAppQuery,
  usePlansPageQuery, UserSubscriptionFragment,
  useUserCartQuery,
  useUserDetailsQuery,
} from '@hooks/api'
import { useCheckoutFlow } from '@hooks/UseCheckoutFlow'
import { reportCheckoutUiStatusGap } from '@lib/checkoutObservability'
import { CheckoutStepper, SectionLoading, useForm } from '@molecules/index'
import { CheckOutFlowTypeEnum, DeviceTypeEnum } from '@uctypes/api/globalTypes'
import { getOnDemandSteps, getSafeShouldPresentCheckoutUI, getSubscriptionSteps, isCheckoutStatusReady, shouldRenderCheckoutStep, StepConfig } from '@utility/CheckoutPure'
import * as checkoutStorage from '@utility/CheckoutStorage'
import { checkoutLogger } from '@utility/Logger'
import { SearchEngineOptimization } from '@utility/SearchEngineOptimization'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  ${ResponsivePXValue('padding-bottom', { mobile: '16px', tablet: '16px', desktop: '40px' })}

  ${ResponsivePXValue('max-width', { mobile: '100vw', tablet: '100vw', desktop: '100%' })}

  .plans-desc{
    text-wrap: balance;
    ${ResponsivePXValue('min-width', { mobile: '70vw', tablet: 'initial', desktop: 'initial' })}
  }

  .price {
     ${ResponsivePXValue('padding-top', { mobile: '4px', tablet: '4px', desktop: '8px' })}
     ${ResponsivePXValue('padding-right', '4px')}
  }

  .free-delivery {
    font-style: italic;

     color: ${(props): string => props.theme.colors.greys.tundora};
  }

   .plan-copyright {
    ${ResponsivePXValue('margin-bottom', { desktop: '85px' })}
  }

  .checkout-section {
  }

`

const SectionContainer = styled.div<{ $background: string, $shouldPresentCheckoutUI?: boolean, $showStepLoadingOverlay?: boolean }>`
 display: flex;
 flex-direction: column;
 align-items: center;

 position: relative;
 transition: opacity 0.2s;

 opacity: ${(props) => (props.$showStepLoadingOverlay ? 0.7 : 1)};
 background: ${(props): string => props.$background};

 ${ResponsivePXValue('padding-top', { desktop: '16px', tablet: '12px', mobile: '8px' })};

  ${({ $shouldPresentCheckoutUI }) => $shouldPresentCheckoutUI && css`
   ${ResponsivePXValue('padding-top', { desktop: '150px', tablet: '150px', mobile: '102px' })};
  `}

   .next-button {
     ${ResponsiveProperty('align-self', { mobile: 'center', tablet: 'flex-end', desktop: 'flex-end' })}
     ${ResponsivePXValue('min-width', '180px')}
   }

   form {
      display: flex;
      flex-direction: column;

     .radio-group-container {
       ${ResponsiveProperty('flex-direction', { mobile: 'column', tablet: 'column', desktop: 'row' })}
       justify-content: center;
     }

     .radio-item-container {
       ${PlansBoxShadow}
     }
   }

  .people-radio {
   flex: 1;
  }
  .dishes-radio {
    flex: 1;

     ${ResponsivePXValue('margin-left', { desktop: '48px' })}
     ${ResponsivePXValue('max-width', { desktop: '500px' })}
  }

  .category-radio {

    .radio-group-container {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      ${ResponsivePXValue('gap', { desktop: '16px' })}
    }

    .radio-item-container {
      ${ResponsiveProperty('flex', { desktop: '0 1 240px', tablet: '1', mobile: '1' })}
      ${ResponsivePXValue('min-width', { desktop: '216px' })}
      ${ResponsivePXValue('max-width', { mobile: '60%', tablet: '30%', desktop: '24%' })}
    }

    .container {
      justify-content: center;
    }
   
    .item-label {
       ${ResponsivePXValue('margin', { mobile: '0 12px 0 4px', tablet: '0 0 0 12px', desktop: '0 0 0 24px' })}
    }
  }


`

const NotFoundMessage = styled.div`
  ${NotFound}
`

const RelativeContainer = styled.div`
  position: relative;
  `
// --- Fade transition styled component ---
const FadeTransition = styled.div<{ $show: boolean }>`
  opacity: ${props => (props.$show ? 1 : 0)};
  position: absolute;
  width: 100%;
  pointer-events: auto;
  z-index: 2;
  transition: opacity 0.35s cubic-bezier(0.4,0,0.2,1);
  will-change: opacity;
`

const StepLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${(props): string => props.theme.colors.misc.overlayBackground};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  transition: background 0.2s;
`

const seo = {
  name: 'UCOOK Meal Kit Plans',
  title: 'Meal Plans with UCOOK\'s Meal Kits Plan | UCOOK',
  meta: [{
    name: 'description',
    content: 'Get started with Easy Peasy, Health Nut and Vegetarian meal options, each tailored to suit your lifestyle.',
  }, {
    name: 'keywords',
    content: 'Meal Plan Box,Subscription Meal Kits,Dinner Plan Box',
  }, {
    name: 'robots',
    content: 'index,follow',
  }],
}

export function MealKitPlans(): JSX.Element {

  const theme = useTheme()
  const config = useConfig()
  const [form] = useForm()
  const { addToast } = useToasts()
  const { data: plansData, loading } = usePlansPageQuery()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()

  // --- Remove Apollo fetch policy override and cache debug logs ---
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const { data: userCartData, refetch: refetchUserCart } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const hasReportedStatusGapRef = useRef(false)
  const hasTriggeredCartRefetchRef = useRef(false)

  const plansMealKitCategories = plansData?.categories?.list
  const checkoutStatus = userCartData?.currentUser?.checkoutStatus
  const shouldPresentCheckoutUI = getSafeShouldPresentCheckoutUI(checkoutStatus)
  const sessionFlowType = checkoutStorage.getCheckoutSession('checkoutFlowType')

  // --- Determine checkoutFlowType from sessionStorage (on-demand) or fallback to API (subscription) ----
  let initialCheckoutFlowType = CheckOutFlowTypeEnum.SUBSCRIPTION

  if (sessionFlowType === CheckOutFlowTypeEnum.ON_DEMAND) {
    initialCheckoutFlowType = CheckOutFlowTypeEnum.ON_DEMAND
    checkoutStorage.clearCheckoutSession('checkoutFlowType')
  }

  // Memoize steps arrays to prevent infinite update loop in useCheckoutFlow
  const subscriptionSteps = useMemo(getSubscriptionSteps, []) as StepConfig[]
  const onDemandSteps = useMemo(getOnDemandSteps, []) as StepConfig[]

  // Pass showSignUpModal and addToast to useCheckoutFlow, and use the guarded handler it returns
  const checkout = useCheckoutFlow({
    subscriptionSteps,
    onDemandSteps,
    plansMealKitCategories,
    userCartData,
    // appData,
    userDetailsData,
    addToast,
    form,
    checkoutStatus,
    shouldPresentCheckoutUI,
    initialCheckoutFlowType,
  })

  const { state, setState, currentSteps, isGuestUser, isCheckoutStatusLoading, isHydrated, isStepReady, handleGoToStepGuarded, actionTitle } = checkout
  const isMobile = appData.app.deviceType === DeviceTypeEnum.MOBILE
  // --- Show loading overlay if transitioning ---
  const showStepLoadingOverlay = state.pageLoading || isCheckoutStatusLoading || !isStepReady
  // For ON_DEMAND, always show stepper and renderer; for subscription, use shouldPresentCheckoutUI. If native app, never show stepper
  const displayCheckoutStepper = state.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND || shouldPresentCheckoutUI

  // Extract the current subscription for ref-based hydration
  const registeredUser = userDetailsData?.currentUser as RegisteredUserDetailsFragment | undefined
  const activeSubscriptionId = userCartData?.currentUser?.activeMenu?.subscription?.id
  const subscription: UserSubscriptionFragment | undefined = registeredUser?.subscriptions?.find((sub) => sub.id === activeSubscriptionId)
  const defaultCreditCardId = registeredUser?.creditCards?.find((card) => card.isDefault === true)?.id
  const defaultAddressId = registeredUser?.addresses?.find((address) => address.isDefault === true)?.id

  const [displayedStep, setDisplayedStep] = useState(() => {
    // Clamp initial value
    return Math.max(0, Math.min(state.currentStep, currentSteps.length - 1))
  })

  // --- Explicit step progression logic based on checkoutStatus and shouldPresentCheckoutUI ---
  // [REMOVED: Step progression useEffect, now handled in useCheckoutFlow]

  // --- [CHECKOUT_DEBUG] Log checkoutStatus from API in for QA ---
  useEffect(() => {
    if (checkoutStatus) {
      checkoutLogger.logBooleans(checkoutStatus, '[API_CHECKOUT_STATUS]', 'id')
      checkoutLogger.info('initialCheckoutFlowType =>', initialCheckoutFlowType, 'green')
    }
  }, [checkoutStatus, initialCheckoutFlowType])

  // Refetch cart once when registered user lands on checkout without checkoutStatus (post-signup race)
  useEffect(() => {
    const isRegistered = userDetailsData?.currentUser?.__typename === 'RegisteredUser'
    const checkoutPathActive =
      initialCheckoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND || sessionFlowType === CheckOutFlowTypeEnum.ON_DEMAND
    if (
      isRegistered &&
      checkoutPathActive &&
      !isCheckoutStatusReady(checkoutStatus) &&
      !hasTriggeredCartRefetchRef.current
    ) {
      hasTriggeredCartRefetchRef.current = true
      refetchUserCart().catch(() => undefined)
    }
  }, [checkoutStatus, initialCheckoutFlowType, refetchUserCart, sessionFlowType, userDetailsData?.currentUser?.__typename])

  useEffect(() => {
    const isRegistered = userDetailsData?.currentUser?.__typename === 'RegisteredUser'
    const checkoutPathActive =
      initialCheckoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND || sessionFlowType === CheckOutFlowTypeEnum.ON_DEMAND
    if (
      isRegistered &&
      checkoutPathActive &&
      !isCheckoutStatusReady(checkoutStatus) &&
      !hasReportedStatusGapRef.current
    ) {
      hasReportedStatusGapRef.current = true
      reportCheckoutUiStatusGap({
        surface: 'MealKitPlans',
        reason: 'status_missing_on_plans_mount',
        sessionFlowType: sessionFlowType ?? null,
        stateFlowType: initialCheckoutFlowType,
        hasCurrentUser: !!userCartData?.currentUser,
        hasCheckoutStatusId: false,
        isGuest: userDetailsData?.currentUser?.__typename === 'GuestUser',
        isHydrated: false,
        isCheckoutStatusLoading: true,
      })
    }
  }, [
    checkoutStatus,
    initialCheckoutFlowType,
    sessionFlowType,
    userCartData?.currentUser,
    userDetailsData?.currentUser?.__typename,
  ])

  // Always sync displayedStep immediately with state.currentStep, clamped
  useEffect(() => {
    const clamped = Math.max(0, Math.min(state.currentStep, currentSteps.length - 1))
    if (displayedStep !== clamped) {
      setDisplayedStep(clamped)
    }
  }, [state.currentStep, displayedStep, currentSteps.length])

  const handleConfirmDetailsWithDefaults = async () => {
    await checkout.handleConfirmDetails(defaultCreditCardId, defaultAddressId)
  }

  const _handleOnCardClose = () => {}

  /* Only render CheckoutStepRenderer if all conditions are met */
  const shouldRenderCheckoutStepRenderer = shouldRenderCheckoutStep(
    displayedStep,
    currentSteps,
    state,
    shouldPresentCheckoutUI,
    checkoutStatus,
  )

  return (
    <>
      <SearchEngineOptimization seo={seo} />
      <Container>
        <Choose>
          <When condition={!isHydrated}>
            <FadeTransition $show={true}>
              <SectionLoading />
            </FadeTransition>
          </When>
          <When condition={loading}>
            <SectionLoading />
          </When>
          <When condition={!plansData}>
            <NotFoundMessage>
              No Data Found
            </NotFoundMessage>
          </When>

          <Otherwise>
            <>
              <If condition={displayCheckoutStepper}>
                <CheckoutStepper
                  steps={currentSteps}
                  currentStepIndex={displayedStep}
                  maxCompletedStepIndex={state.maxCompleted}
                  onStepSelect={handleGoToStepGuarded}
                  flowType={state.checkoutFlowType as CheckOutFlowTypeEnum}
                  forceCompletedStepIndexes={
                    state.categoryId && displayedStep === 1 ? [1] : []
                  }
                  categoryId={state.categoryId}
                />
              </If>

              <SectionContainer $background={theme.colors.greys.cultured}
                $shouldPresentCheckoutUI={displayCheckoutStepper}
                $showStepLoadingOverlay={showStepLoadingOverlay}
              >
                <Spacer mobile='8px' tablet='74px' desktop='32px' />

                <If condition={shouldRenderCheckoutStepRenderer}>
                  <RelativeContainer>
                    <CheckoutStepRenderer
                      currentStepObj={currentSteps[displayedStep]}
                      state={state}
                      form={form}
                      actionTitle={actionTitle}
                      isMobile={isMobile}
                      setState={setState}
                      isGuestUser={isGuestUser}
                      checkoutStatus={checkoutStatus}
                      handleOnCardClose={_handleOnCardClose}
                      handleChange={checkout.handleChange}
                      handleConfirmDetails={handleConfirmDetailsWithDefaults}
                      handleUpdateSubscription={checkout.handleUpdateSubscription}
                      subscription={subscription}
                      handleGoToStepGuarded={handleGoToStepGuarded}
                      currentSteps={currentSteps}
                    />

                    <If condition={showStepLoadingOverlay}>
                      <StepLoadingOverlay>
                        <SectionLoading />
                      </StepLoadingOverlay>
                    </If>
                  </RelativeContainer>
                </If>
              </SectionContainer>
            </>
          </Otherwise>
        </Choose>
      </Container>
    </>
  )
}
